import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
  Paper,
  Divider,
  Tooltip,
  Badge,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Schedule as ScheduleIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Devices as DevicesIcon,
  DriveEta as VehicleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Today as TodayIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Circle as CircleIcon,
  Error as ErrorIcon,
  Inventory as StockIcon,
  Build as TechnicianIcon,
  Map as MapIcon,
  Update as UpdateIcon,
  PriorityHigh as PriorityIcon,
} from '@mui/icons-material';
import { useApi } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { DashboardKPIs, DailyAction, SystemStats } from '../../types';
import { useNavigate } from 'react-router-dom';

interface ActionItemProps {
  action: DailyAction;
  onAction: (action: DailyAction) => void;
}

const ActionItem: React.FC<ActionItemProps> = ({ action, onAction }) => {
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#f44336';
      case 'high': return '#ff9800';
      case 'medium': return '#2196f3';
      case 'low': return '#4caf50';
      default: return '#757575';
    }
  };

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'device_offline': return <ErrorIcon />;
      case 'renewal_urgent': return <UpdateIcon />;
      case 'installation_pending': return <TechnicianIcon />;
      case 'document_expiring': return <WarningIcon />;
      default: return <AssignmentIcon />;
    }
  };

  const getActionTypeLabel = (type: string) => {
    switch (type) {
      case 'device_offline': return 'Dispositivo Offline';
      case 'renewal_urgent': return 'Renovación Urgente';
      case 'installation_pending': return 'Instalación Pendiente';
      case 'document_expiring': return 'Documento por Vencer';
      case 'sim_expiring': return 'SIM por Vencer';
      default: return 'Acción Requerida';
    }
  };

  return (
    <Card sx={{ mb: 2, border: `2px solid ${getPriorityColor(action.priority)}` }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <Avatar sx={{ bgcolor: getPriorityColor(action.priority) }}>
              {getActionIcon(action.type)}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {action.title}
                </Typography>
                <Chip
                  label={getActionTypeLabel(action.type)}
                  size="small"
                  color={action.priority === 'critical' ? 'error' : action.priority === 'high' ? 'warning' : 'info'}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {action.description}
              </Typography>
              {action.dueDate && (
                <Typography variant="caption" color="text.secondary">
                  Vence: {new Date(action.dueDate).toLocaleDateString('es-DO')}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => onAction(action)}
              color={action.priority === 'critical' ? 'error' : 'primary'}
            >
              Atender
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const AssistantDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const {
    data: kpis,
    loading: kpisLoading,
    error: kpisError,
    execute: fetchKpis,
  } = useApi<DashboardKPIs>(() => apiService.getDashboardKPIs());

  const {
    data: dailyActionsData,
    loading: actionsLoading,
    error: actionsError,
    execute: fetchDailyActions,
  } = useApi<{ date: string; totalActions: number; byPriority: Record<string, number>; actions: DailyAction[] }>(() =>
    apiService.getDailyActions()
  );

  const {
    data: systemStats,
    loading: systemLoading,
    execute: fetchSystemStats,
  } = useApi<SystemStats>(() => apiService.getSystemStats());

  useEffect(() => {
    fetchKpis();
    fetchDailyActions();
    fetchSystemStats();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchKpis(),
        fetchDailyActions(),
        fetchSystemStats(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleForceSync = async (type: 'devices' | 'positions' | 'offline-detection' | 'renewals') => {
    try {
      await apiService.forcSync(type);
      // Refresh data after sync
      setTimeout(() => {
        fetchKpis();
        fetchDailyActions();
      }, 2000);
    } catch (error) {
      console.error('Error forcing sync:', error);
    }
  };

  const handleActionClick = (action: DailyAction) => {
    switch (action.type) {
      case 'device_offline':
        navigate(`/devices/${action.entityId}`);
        break;
      case 'renewal_urgent':
        navigate(`/renewals/${action.entityId}`);
        break;
      case 'installation_pending':
        navigate(`/devices/groups/technician`);
        break;
      default:
        navigate('/dashboard');
    }
  };

  const getPriorityActions = () => {
    if (!dailyActionsData?.actions) return [];
    return dailyActionsData.actions
      .filter(action => action.priority === 'critical' || action.priority === 'high')
      .slice(0, 8);
  };

  // Debug: log the current state of kpis
  console.log('AssistantDashboard - KPIs data:', kpis);
  console.log('AssistantDashboard - KPIs loading:', kpisLoading);
  console.log('AssistantDashboard - KPIs error:', kpisError);

  // Show loading state if KPIs are still loading or if kpis is undefined/null
  if (kpisLoading || !kpis) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  // Show error state if KPIs failed to load
  if (kpisError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error cargando datos del dashboard: {kpisError}
        <Button onClick={() => fetchKpis()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Dashboard Asistente
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestiona dispositivos, clientes y renovaciones del día
          </Typography>
          {systemStats?.traccar && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Chip
                icon={<CircleIcon sx={{ fontSize: 12 }} />}
                label={`Traccar: ${systemStats.traccar.status === 'connected' ? 'Conectado' : 'Desconectado'}`}
                size="small"
                color={systemStats.traccar.status === 'connected' ? 'success' : 'error'}
              />
              <Typography variant="caption" color="text.secondary">
                Última sync: {systemStats.traccar.lastSync ? new Date(systemStats.traccar.lastSync).toLocaleTimeString('es-DO') : 'N/A'}
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Sincronizar Dispositivos">
            <IconButton onClick={() => handleForceSync('devices')} size="small">
              <SyncIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* KPIs */}
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2}>
            {/* Devices KPIs */}
            <Grid size={{ xs: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <DevicesIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {kpis?.devices?.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Dispositivos Total
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                    <Chip
                      label={`${kpis?.devices?.online || 0} Online`}
                      size="small"
                      color="success"
                    />
                    <Chip
                      label={`${kpis?.devices?.offline || 0} Offline`}
                      size="small"
                      color="error"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Device Groups */}
            <Grid size={{ xs: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <StockIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {kpis?.devices?.byGroup?.stock || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    En Stock
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                    <Chip
                      label={`${kpis?.devices?.byGroup?.technician || 0} Técnicos`}
                      size="small"
                      color="info"
                    />
                    <Chip
                      label={`${kpis?.devices?.byGroup?.client || 0} Clientes`}
                      size="small"
                      color="success"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Clients */}
            <Grid size={{ xs: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PersonIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {kpis?.clients?.total || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Clientes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {kpis?.clients?.newThisMonth || 0} nuevos este mes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Renewals */}
            <Grid size={{ xs: 6, md: 3 }}>
              <Card sx={{ backgroundColor: kpis?.renewals?.overdue ? 'error.50' : 'inherit' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <UpdateIcon sx={{
                    fontSize: 40,
                    color: kpis?.renewals?.overdue ? 'error.main' : 'secondary.main',
                    mb: 1
                  }} />
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 600 }}
                    color={kpis?.renewals?.overdue ? 'error.main' : 'inherit'}
                  >
                    {kpis?.renewals?.dueThisMonth || 0}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Renovaciones Este Mes
                  </Typography>
                  {kpis?.renewals?.overdue ? (
                    <Chip
                      label={`${kpis.renewals.overdue} Vencidas`}
                      size="small"
                      color="error"
                      sx={{ mt: 1 }}
                    />
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Quick Actions */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Acciones Rápidas
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => navigate('/clients/new')}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  Nuevo Cliente
                </Button>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DevicesIcon />}
                  onClick={() => navigate('/assistant/devices')}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  Asignar Dispositivos
                </Button>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DevicesIcon />}
                  onClick={() => navigate('/devices/new')}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  Registrar GPS
                </Button>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<VehicleIcon />}
                  onClick={() => navigate('/vehicles/new')}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  Registrar Vehículo
                </Button>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<MapIcon />}
                  onClick={() => navigate('/devices/map')}
                  sx={{ py: 1.5, justifyContent: 'flex-start' }}
                >
                  Ver Mapa
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* System Status */}
          {systemStats && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Estado del Sistema
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Base de Datos</Typography>
                  <Chip
                    label={systemStats.database?.status === 'connected' ? 'Conectada' : 'Desconectada'}
                    size="small"
                    color={systemStats.database?.status === 'connected' ? 'success' : 'error'}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Jobs Activos</Typography>
                  <Badge badgeContent={systemStats.jobs?.active || 0} color="primary">
                    <Chip label="Procesando" size="small" />
                  </Badge>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Dispositivos Traccar</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {systemStats.traccar?.devices || 0}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Daily Actions */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Acciones del Día
                </Typography>
                {dailyActionsData && (
                  <Typography variant="body2" color="text.secondary">
                    {dailyActionsData.totalActions} acciones • {dailyActionsData.byPriority.critical || 0} críticas
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/devices/groups')}
                >
                  Gestionar Grupos
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/renewals')}
                >
                  Ver Renovaciones
                </Button>
              </Box>
            </Box>

            {actionsLoading ? (
              <Box sx={{ py: 4 }}>
                <LinearProgress />
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
                  Cargando acciones del día...
                </Typography>
              </Box>
            ) : actionsError ? (
              <Alert severity="error">
                Error cargando acciones: {actionsError}
              </Alert>
            ) : getPriorityActions().length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                  ¡Todo al día!
                </Typography>
                <Typography color="text.secondary">
                  No hay acciones prioritarias pendientes
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
                {getPriorityActions().map((action) => (
                  <ActionItem
                    key={action.id}
                    action={action}
                    onAction={handleActionClick}
                  />
                ))}
                {dailyActionsData && dailyActionsData.actions.length > 8 && (
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Button
                      variant="text"
                      onClick={() => navigate('/actions')}
                    >
                      Ver todas las {dailyActionsData.totalActions} acciones
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Device Workflow */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Flujo de Dispositivos
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ backgroundColor: 'warning.50', cursor: 'pointer' }} onClick={() => navigate('/assistant/devices')}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <StockIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                      {kpis?.devices?.byGroup?.stock || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Not_Installed
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dispositivos disponibles para asignar
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ backgroundColor: 'info.50', cursor: 'pointer' }} onClick={() => navigate('/devices/groups/technician')}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <TechnicianIcon sx={{ fontSize: 48, color: 'info.main', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                      {kpis?.devices?.byGroup?.technician || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Con Técnicos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Listos para instalación
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Card sx={{ backgroundColor: 'success.50', cursor: 'pointer' }} onClick={() => navigate('/devices/groups/client')}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <PersonIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                      {kpis?.devices?.byGroup?.client || 0}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Con Clientes
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Dispositivos activos
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssistantDashboard;